import { ArrowRight } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { spacing } from '@/constants/theme';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import { MAX_PICKS } from '@/stores/voltrUploadTypes';
import type { StepVideo } from '@/types/deal';

import { UploadAddBox } from './UploadAddBox';
import { UploadFileCard } from './UploadFileCard';
import { UploadPickBox } from './UploadPickBox';

/** The page's scroll id for this panel: the hero's "Upload content" lands here. */
export const UPLOADER_ID = 'uploader';

export type SocialInlineUploadProps = {
  actionId: number;
  /** The brand's video rights the athlete agrees to by submitting; null until /social has them. */
  rightsText: string | null;
  /** Opens the camera roll; `replaces` = the sent-back video's id. New picks join those waiting. */
  pick: (replaces?: string) => void;
  pickError: string | null;
  /** Why uploads can't start (the brand's page or its video rights didn't load), with a retry. */
  rightsProblem?: string | null;
  onRetry?: () => void;
  /** The server's "Add another video" once there are videos; the box reads "Upload content" before. */
  addLabel?: string;
  /** The step's videos, to name the one being replaced. */
  videos?: StepVideo[];
  register?: (id: string, view: View | null) => void;
  testID?: string;
};

/**
 * The upload step's own uploader (Figma "Create and upload content"): the
 * dashed box opens the camera roll; each pick gets a file card (Uploading
 * 62% with Cancel, or Ready to submit with a ✕); "Add another video" under
 * them adds more before Submit, which sends them, agreeing to the brand's
 * video rights written above it. A video Voltr has ready leaves the list: the
 * server puts it on the step.
 */
export const SocialInlineUpload = observer(function SocialInlineUpload({
  actionId,
  rightsText,
  pick,
  pickError,
  rightsProblem,
  onRetry,
  addLabel,
  videos,
  register,
  testID = 'social-upload',
}: SocialInlineUploadProps) {
  const store = voltrUploadStore;
  const ours = store.isFor(actionId);
  const items = ours ? store.items : [];
  const busy = store.running && ours;
  // Another deal's upload still going: this one waits.
  const elsewhere = store.busyElsewhere(actionId);
  // A finished pick has no card, so it mustn't keep counting against the picks left or the same-file check.
  const anyDone = items.some((i) => i.phase === 'done');
  useEffect(() => {
    if (anyDone && !busy) store.dropDone();
  }, [anyDone, busy, store]);

  const replaces = ours ? store.context?.replaces : undefined;
  const replacing = replaces ? videos?.find((v) => v.id === replaces)?.name : undefined;
  const unsentCount = items.filter((i) => i.phase === 'idle' || i.phase === 'error').length;
  const unsent = unsentCount > 0;
  const failed = items.some((i) => i.phase === 'error');
  // More can join before Submit; a replacement is one video.
  const canAdd = items.length > 0 && items.length < MAX_PICKS && !busy && !replaces;

  return (
    <View ref={register ? (view) => register(UPLOADER_ID, view) : undefined} style={styles.panel} testID={testID}>
      {items.length === 0 ? (
        <UploadPickBox title={addLabel ?? 'Upload content'} onPress={() => pick()} disabled={!rightsText || elsewhere} testID={`${testID}-pick`} />
      ) : null}
      {replacing ? (
        <Text variant="caption" color="textMuted" testID={`${testID}-replacing`}>
          Replaces {replacing}
        </Text>
      ) : null}
      {/* A finished video is on the step's own list by now: one card each. */}
      {items.map((item, i) =>
        item.phase === 'done' ? null : (
          <UploadFileCard
            key={item.key}
            item={item}
            onCancel={() => store.cancel()}
            onRemove={busy || item.jobId ? undefined : () => store.remove(item.key)}
            testID={`${testID}-item-${i}`}
          />
        ),
      )}
      {canAdd ? <UploadAddBox onPress={() => pick()} disabled={!rightsText || elsewhere} testID={`${testID}-add`} /> : null}
      {elsewhere ? <Note testID={`${testID}-elsewhere`}>Another upload is still going. Wait for it to finish.</Note> : null}
      {rightsProblem ? (
        <>
          <Note testID={`${testID}-blocked`}>{rightsProblem}</Note>
          {onRetry ? <Button label="Try again" variant="outline" fullWidth onPress={onRetry} testID={`${testID}-retry`} /> : null}
        </>
      ) : null}
      {pickError ? (
        <Text variant="caption" color="danger" testID={`${testID}-pick-error`}>
          {pickError}
        </Text>
      ) : null}
      {!busy && items.some((i) => i.phase === 'waiting') ? (
        <Button label="Check again" variant="outline" fullWidth onPress={() => void store.checkWaiting()} testID={`${testID}-check`} />
      ) : null}
      {unsent && rightsText && !busy ? (
        <>
          <Text variant="caption" color="textMuted" testID={`${testID}-rights`}>
            By submitting, you confirm: {rightsText}
          </Text>
          <Button
            label={failed ? 'Try again' : unsentCount > 1 ? `Submit ${unsentCount} videos` : 'Submit'}
            fullWidth
            trailingIcon={ArrowRight}
            onPress={() => void store.uploadAll()}
            testID={`${testID}-submit`}
          />
        </>
      ) : null}
    </View>
  );
});

function Note({ children, testID }: { children: string; testID: string }) {
  return (
    <Text variant="caption" color="textMuted" testID={testID}>
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  panel: { gap: spacing.sm, marginTop: spacing.sm },
});
